import {
  DatabaseAuto,
  IbulkDelete,
  IbulkDeleteSuccessResponse,
  IdataArrayResponse,
  IdeleteOneResponse,
  Ifile, IfilterProps, Isuccess, TbackEndGeneratedFields,
  TbackEndGeneratedFieldsExcludingId
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { IcommonManager, IdeliveryManger, Imanager } from '../../../shared-types';
import { ManagerClient } from '../../utils/manager-client';

/**
 * Interface representing a manager base omit.
 */
export interface ImanagerBaseOmit<CreateMode extends boolean = false> {
  manager: CreateMode extends true ? Omit<Imanager, TbackEndGeneratedFields | 'defaultCurrency' | 'authUser'> :
  Omit<Partial<Imanager>, TbackEndGeneratedFields | 'defaultCurrency' | 'authUser'>;
}

/**
 * Type representing a deliver manager request.
 */
export type TdeliverManagerRequest<
  CreateMode extends boolean = false, U = Omit<Partial<IdeliveryManger>,
  TbackEndGeneratedFieldsExcludingId | 'manager'> & ImanagerBaseOmit<CreateMode>> =
  CreateMode extends true ? Omit<
  IdeliveryManger, TbackEndGeneratedFields | 'manager'> & ImanagerBaseOmit<CreateMode> : U;

/**
 * Abstract class representing a manager base.
 */
export abstract class ManagerBase
  extends DatabaseAuto {
  /**
   * The ID of the user.
   */
  urId: string;

  /**
   * The bio of the user.
   */
  bio: string;

  /**
   * The title of the user.
   */
  title: string;

  /**
   * The manager of the user.
   */
  manager: Imanager;

  /**
   * The base URL for the manager.
   */
  baseUrl = '';

  constructor(data: IcommonManager) {
    super(data);
    this.manager = data.manager;
    this.urId = data.urId;
    this.bio = data.bio;
    this.title = data.title;
  }

  /**
   * Changes the currency for the current user.
   * @param currency - The currency to change to.
   * @returns A promise resolving to a success object.
   */
  changeUserCurrency(currency: string) {
    const observer$ = ManagerClient.ehttp.makePost<Isuccess>(this.baseUrl + '/change-currency', { currency });

    return lastValueFrom(observer$);
  }

  /**
   * Invites a new deliverymanager member.
   *
   * @param {Object} payload - The invitation data.
   * @param {string} payload.email - The email address of the deliverymanager member to invite.
   * @param {string} payload.phone - The phone number of the deliverymanager member to invite.
   * @param {string} payload.phoneCode - The phone code of the deliverymanager member to invite.
   * @param {string} payload.method - The method of invitation (e.g., 'email', 'sms').
   * @param {string} payload.deliverymanagerId - The ID of the deliverymanager member to invite.
   * @returns {Promise<{ qrCodeUrl?: string }>} The result of the invitation operation.
   */
  invite(payload: {
    email: string; phone: string;
    phoneCode: string; method: string;
    managerId: string; }): Promise<{ qrCodeUrl?: string }> {
    const observer$ = ManagerClient.ehttp
      .makePost<{ qrCodeUrl?: string }>(`${this.baseUrl}/invite`, payload);

    return lastValueFrom(observer$);
  }
}

export class DeliveryManager
  extends ManagerBase {
  static baseUrl = '/deliverymanager';
  ordersAssigned: string[];
  override baseUrl = '/deliverymanager';

  constructor(data: IdeliveryManger) {
    super(data);

    this.ordersAssigned = data.ordersAssigned || [];
  }

  /**
   * Retrieves a list of delivery managers.
   * @param {number} [offset=0] - The offset to start retrieving delivery managers from.
   * @param {number} [limit=0] - The limit of delivery managers to retrieve.
   * @returns {Promise<{count: number, dataArr: DeliveryManager[]}>} - A promise that
   *  resolves to an object containing the total count of delivery managers
   * and an array of delivery manager objects.
   */
  static async getAll(offset = 0, limit = 0) {
    const observer$ = ManagerClient.ehttp
      .makeGet<IdataArrayResponse<IdeliveryManger>>(`${DeliveryManager.baseUrl}/all/${offset}/${limit}`);
    const deliverymanagers = await lastValueFrom(observer$);

    return {
      count: deliverymanagers.count,
      dataArr: deliverymanagers.data
        .map(val => new DeliveryManager(val)) };
  }

  /**
   * Retrieves a list of delivery managers based on a filter.
   * @param {IfilterProps} filter - The filter to apply to the delivery managers.
   * @returns {Promise<{count: number, dataArr: DeliveryManager[]}>} - A promise that
   *  resolves to an object containing the total count of delivery managers
   * and an array of delivery manager objects.
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = ManagerClient.ehttp
      .makePost<IdataArrayResponse<IdeliveryManger>>(`${DeliveryManager.baseUrl}/filter`, filter);
    const deliverymanagers = await lastValueFrom(observer$);

    return {
      count: deliverymanagers.count,
      dataArr: deliverymanagers.data
        .map(val => new DeliveryManager(val)) };
  }

  /**
   * Retrieves a delivery manager by ID.
   * @param {string} resourceId - The ID of the delivery manager to retrieve.
   * @returns {Promise<DeliveryManager>} - A promise that resolves to a delivery manager object.
   */
  static async getOne(resourceId: string) {
    const observer$ = ManagerClient.ehttp
      .makeGet<IdeliveryManger>(`${DeliveryManager.baseUrl}/getone/${resourceId}`);
    const deliverymanager = await lastValueFrom(observer$);

    return new DeliveryManager(deliverymanager);
  }

  /**
   * Retrieves the current user's profile.
   * @returns {Promise<DeliveryManager>} - A promise that resolves to a delivery manager object.
   */
  static async myProfile() {
    const observer$ = ManagerClient.ehttp
      .makePost<IdeliveryManger>(`${DeliveryManager.baseUrl}/my-profile`, {});
    const deliverymanager = await lastValueFrom(observer$);

    return new DeliveryManager(deliverymanager);
  }

  /**
   * Adds a new delivery manager.
   * @param {TdeliverManagerRequest<true>} payload - The payload containing the delivery manager data.
   * @returns {Promise<DeliveryManager>} - A promise that resolves to a delivery manager object.
   */
  static async add(payload: TdeliverManagerRequest<true>) {
    const observer$ = ManagerClient.ehttp.makePost<IdeliveryManger>(`${DeliveryManager.baseUrl}`, payload);

    return new DeliveryManager(await lastValueFrom(observer$));
  }

  /**
   * Deletes multiple delivery managers based on the provided IDs.
   * @param {IbulkDelete} vals - The IDs of the delivery managers to delete.
   * @returns {Promise<IbulkDeleteSuccessResponse>} - A promise that resolves to a success object.
   */
  static bulkDelete(vals: IbulkDelete) {
    const observer$ = ManagerClient.ehttp
      .makePut<IbulkDeleteSuccessResponse>(`${DeliveryManager.baseUrl}/bulk-delete`, vals);

    return lastValueFrom(observer$);
  }

  /**
   * Validates a delivery manager invitation token.
   * @param {Object} payload - The payload containing the token to validate.
   * @param {string} payload.token - The invitation token to validate.
   * @returns {Promise<Isuccess>} The result of the validation operation.
   */
  static validateInvitation(payload: { token: string }): Promise<Isuccess> {
    const observer$ = ManagerClient.ehttp
      .makePost<Isuccess>(`${DeliveryManager.baseUrl}/validate-invitation`, payload);

    return lastValueFrom(observer$);
  }

  /**
   * Uploads multiple files to the deliverymanager member.
   *
   * @param {Ifile[]} files - The files to upload.
   * @returns {Promise<Isuccess>} The result of the file upload operation.
   */
  uploadImages(files: Ifile[]) {
    return ManagerClient.ehttp
      .uploadFiles<IdeliveryManger>(files, `${DeliveryManager.baseUrl}/upload-images`, { _id: this._id });
  }

  /**
   * Uploads profilePicture associated with the current DeliveryManager instance.
   * @param file - The image to upload.
   * @returns An observable that emits a success object.
   */
  uploadProfilePicture(file: Ifile) {
    return ManagerClient.ehttp
      .uploadFiles<IdeliveryManger>([file], `${DeliveryManager.baseUrl}/upload-profile-picture`, { _id: this._id });
  }

  /**
   * Uploads cover pic associated with the current DeliveryManager instance.
   * @param file - The image to upload.
   * @returns An observable that emits a success object.
   */
  uploadProfileCoverPicture(file: Ifile) {
    return ManagerClient.ehttp
      .uploadFiles<
      IdeliveryManger>([file], `${DeliveryManager.baseUrl}/upload-profile-cover-picture`, { _id: this._id });
  }

  async setImageAs(
    fileId: string,
    field: 'profilePicture' | 'profileCoverPicture'
  ) {
    const observer$ = ManagerClient.ehttp
      .makePost<IdeliveryManger>(`${DeliveryManager.baseUrl}/set-image-as`, { fileId, field });

    return new DeliveryManager(await lastValueFrom(observer$));
  }

  /**
   * Deletes images associated with the current user.
   *
   * @param {string[]} fileIds - The IDs of the files to delete.
   * @returns {Promise<DeliveryManager>} The result of the deletion operation.
   */
  async deleteImages(fileIds: string[]) {
    const observer$ = ManagerClient.ehttp
      .makePost<IdeliveryManger>(this.baseUrl + '/delete-images', { fileIds, _id: this._id });

    return new DeliveryManager(await lastValueFrom(observer$));
  }

  /**
   * Updates the delivery manager.
   * @param {TdeliverManagerRequest} payload - The payload containing the delivery manager data.
   * @returns {Promise<DeliveryManager>} - A promise that resolves to a delivery manager object.
   */
  async update(payload: TdeliverManagerRequest) {
    const observer$ = ManagerClient.ehttp
      .makePut<IdeliveryManger>(`${DeliveryManager.baseUrl}`, payload);

    return new DeliveryManager(await lastValueFrom(observer$));
  }

  /**
   * Deletes the delivery manager.
   * @returns {Promise<IdeleteOneResponse | null>} - A promise that resolves to a delete one response object.
   */
  remove() {
    const observer$ = ManagerClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${DeliveryManager.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }
}
